import React, { useEffect } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// 🚨 ONE WAY TO OPEN A ROW, FOR EVERY TABLE THAT HAS ROWS.
//
// Jesse: "optimize and make consistent [how] you expand a row on the Leads,
// Partners, Accounts, and Referrals tables … it should either be a side panel
// with an X to close out or a popup with an x to close out. The Leads pop ups
// are the wrong width too."
//
// 🔑 THERE WERE FOUR ANSWERS TO ONE QUESTION. Partners used this drawer at
// 600px, Meetings at 440, Referrals had a hand-rolled 420px drawer at z-60,
// Accounts had a 640px centre modal AND a 560px one on the same page, and Leads
// had its own 560px full-height panel. Each had solved its own problems
// separately, so none of the fixes travelled.
//
// ⚠️ THIS COMPONENT ALREADY EXISTED AND I NEARLY SHIPPED A SECOND ONE. The first
// pass added components/RowDetailPanel.jsx with the same job, because I wrote it
// before looking for an incumbent — which is precisely the duplication that
// produced the four variants in the first place. It was deleted; the fixes it
// carried were folded in here instead.
//
// ── THE MOBILE BUGS THIS FIXES ──────────────────────────────────────────────
//
// 🚨 A FIXED px WIDTH IS WIDER THAN A PHONE. `width: 600px` on a 390px screen
// put the right-hand edge of the drawer — which is where the close button lives
// — off the side of the display. The panel could be opened and not closed.
// min() needs no breakpoint: below `width` the drawer is simply the whole
// screen, which is the only honest layout at that size.
//
// ⚠️ 100dvh, NOT 100vh. Mobile browser chrome makes vh taller than the visible
// area, so the bottom of a full-height panel sits below the fold.
//
// ⚠️ AND THE NOTCH. top: 64px assumed the navbar is 64px tall; in the iOS
// standalone PWA it is 64px PLUS the safe-area inset, so the drawer started
// underneath the header.
// ─────────────────────────────────────────────────────────────────────────────

// 🔑 `actions` (Leads panel, 2026-11-02): the header's one primary action, between the title and the X — "Resend
// invite" / "Open borrower file" on the approved Leads mockup. Optional; every other caller is unchanged.
export default function RightDrawer({ open, onClose, title, subtitle, tabs, children, footer, actions, width = 420 }) {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.18)',
          zIndex: 1049,
        }}
      />
      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' && title ? title : 'Details'}
        style={{
        position: 'fixed',
        top: 'calc(64px + env(safe-area-inset-top, 0px))',
        right: 0,
        // ⚠️ min(), NOT A FIXED px — see the header. 600px on a 390px phone put
        // the close button off the screen.
        width: `min(${width}px, 100%)`,
        height: 'calc(100dvh - 64px - env(safe-area-inset-top, 0px))',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e5e7eb',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.10)',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          flexShrink: 0,
        }}>
          {/* ⚠️ overflowWrap. A long company name or email in a 390px drawer is
              the common case, and an unbroken one pushes the X off the edge —
              the same failure the width fix above is for. */}
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#111827', margin: 0,
              overflowWrap: 'anywhere' }}>{title}</h3>
            {subtitle ? (
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, overflowWrap: 'anywhere' }}>
                {subtitle}
              </div>
            ) : null}
          </div>
          {actions ? <div style={{ flex: '0 0 auto', display: 'flex', gap: 8, alignItems: 'center' }}>{actions}</div> : null}
          <button
            onClick={onClose}
            type="button"
            aria-label="Close"
            style={{ flex: '0 0 auto', width: 36, height: 36, background: 'none', border: 'none',
              cursor: 'pointer', color: '#9ca3af', fontSize: '22px', lineHeight: 1, borderRadius: '8px' }}
          >×</button>
        </div>
        {/* ⚠️ TABS SIT OUTSIDE THE SCROLL, like the header. A tab bar that
            scrolls away is a tab bar somebody loses — Leads had worked this out
            in its own panel and it is the kind of fix that only travels if the
            component has a slot for it. */}
        {tabs ? <div style={{ flexShrink: 0 }}>{tabs}</div> : null}
        {/* ── 🚨 THE GUTTER IS THE DRAWER'S, NOT EACH PAGE'S ──────────────────
            Jesse: "when you open up a lead row, the formatting of each side
            panel tab is messed up. The margin padding needs to be fixed."

            🚨 FOUR OF THE FIVE PANELS HAD NO GUTTER AT ALL. Read properly —
            Leads, Accounts and Referrals set no horizontal body padding, and
            Meetings set a uniform 20 while Partners set 20 on one toolbar row.
            So content sat flush against both edges, and on Leads that is
            conspicuous because its title and tab bar ARE inset 20px: the header
            and the body of one panel disagreeing, which reads as broken rather
            than merely tight.

            ⚠️ MY FIRST READING SAID "five pages, five answers — 20, 10, 14, 16".
            That came from grepping the FIRST `padding:` inside each drawer's JSX
            range, which on three pages was a button. Acting on it stripped the
            padding off a "View →" button, a link and a blue CTA before the diff
            showed what I had actually hit. The numbers were real; they were not
            describing the thing I said they were.

            🔑 THE SAME SHAPE THIS COMPONENT EXISTS TO END. The header above
            already warns that four pages each solved their own problems
            separately so none of the fixes travelled; a gutter every caller
            re-decides is that again, one property down. 20px matches the header
            and footer in this file, so a panel lines up with itself by
            construction.

            ⚠️ HORIZONTAL ONLY. Vertical space is a per-page judgement — a tab
            bar wants a different top gap than a plain list — so pages keep
            theirs and must not re-add a left or right. */}
        <div style={{
          flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
          padding: '0 20px',
        }}>
          {children}
        </div>
        {footer ? (
          <div style={{
            flexShrink: 0, borderTop: '1px solid #e5e7eb', padding: '12px 20px',
            // ⚠️ THE HOME-INDICATOR INSET, or a footer button sits under the iOS
            // gesture bar.
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          }}>{footer}</div>
        ) : null}
      </div>
    </>
  );
}
